import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  PATCH: 'yellow',
  DELETE: 'red',
};

const EndpointCard = ({ endpoint }) => (
  <Box
    p={5}
    bg="white"
    borderRadius="xl"
    boxShadow="sm"
    borderWidth="1px"
    borderColor="gray.100"
    transition="all 0.2s"
    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
  >
    <Flex align="center" mb={3}>
      <Badge
        colorScheme={methodColors[endpoint.method]}
        px={3}
        py={1}
        borderRadius="full"
        fontWeight="bold"
        fontSize="xs"
      >
        {endpoint.method}
      </Badge>
      <Text ml={3} fontWeight="semibold" fontSize="md" color="gray.700">
        {endpoint.title}
      </Text>
    </Flex>
    <Code
      display="block"
      p={3}
      borderRadius="md"
      bg="gray.50"
      fontSize="sm"
      color="purple.600"
      fontWeight="medium"
    >
      {endpoint.path}
    </Code>
    <Text mt={3} fontSize="sm" color="gray.500">
      {endpoint.description}
    </Text>

    {(endpoint.request || endpoint.response) && (
      <Accordion allowToggle mt={4}>
        {endpoint.request && (
          <AccordionItem border="none">
            <AccordionButton
              px={3}
              py={2}
              bg="blue.50"
              borderRadius="md"
              _hover={{ bg: 'blue.100' }}
            >
              <Box flex="1" textAlign="left">
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                  Request Body
                </Badge>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={2} pt={2}>
              <Code
                display="block"
                p={3}
                borderRadius="md"
                bg="gray.800"
                color="green.300"
                fontSize="xs"
                whiteSpace="pre"
                overflowX="auto"
              >
                {endpoint.request}
              </Code>
            </AccordionPanel>
          </AccordionItem>
        )}
        {endpoint.response && (
          <AccordionItem border="none" mt={endpoint.request ? 2 : 0}>
            <AccordionButton
              px={3}
              py={2}
              bg="green.50"
              borderRadius="md"
              _hover={{ bg: 'green.100' }}
            >
              <Box flex="1" textAlign="left">
                <Badge colorScheme="green" variant="subtle" fontSize="xs">
                  Response
                </Badge>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={2} pt={2}>
              <Code
                display="block"
                p={3}
                borderRadius="md"
                bg="gray.800"
                color="green.300"
                fontSize="xs"
                whiteSpace="pre"
                overflowX="auto"
              >
                {endpoint.response}
              </Code>
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>
    )}
  </Box>
);

const ApiDocs = () => {
  const authEndpoints = [
    {
      title: 'Login',
      method: 'POST',
      path: '/api/v1/auth/login/',
      description: 'Obtain a DRF token by supplying admin credentials. Include the token as Authorization: Token <key> on all subsequent admin API calls.',
      request: `{
  "username": "admin",
  "password": "your-password"
}`,
      response: `{
  "success": true,
  "token": "9944b09199c62bcf9418ad846dd0e4bbdfc6ee4b",
  "username": "admin",
  "email": "admin@example.com"
}`,
    },
    {
      title: 'Ping',
      method: 'GET',
      path: '/api/v1/ping',
      description: 'Public health-check endpoint. No authentication required.',
      response: `{
  "status": "ok",
  "service": "sso-entra"
}`,
    },
    {
      title: 'Check Domain',
      method: 'GET',
      path: '/api/v1/check-domain?domain=acme.com&tenant_id=acme',
      description: 'Check whether a domain is in the allow-list for a given tenant. Used by the main API before initiating an SSO flow.',
      response: `{
  "allowed": true,
  "tenant_id": "acme"
}`,
    },
  ];

  const oauthEndpoints = [
    {
      title: 'Initiate SSO Login',
      method: 'GET',
      path: '/auth/login?tenant=acme',
      description: 'Redirects the browser to Microsoft Entra for authentication. The tenant query param identifies which EntraConfig to use.',
      response: `302 Redirect → https://login.microsoftonline.com/<tenant>/oauth2/v2.0/authorize?...`,
    },
    {
      title: 'OAuth Callback',
      method: 'GET',
      path: '/auth/callback?code=<code>&state=<state>',
      description: 'Microsoft Entra redirects here after the user authenticates. Exchanges the code for claims, resolves the profile, and redirects to the portal with a DRF token in the URL fragment.',
      response: `302 Redirect → https://portal.example.com/sso-callback#<drf-token>

// On failure:
302 Redirect → https://portal.example.com/login?sso_error=1`,
    },
  ];

  const entraConfigEndpoints = [
    {
      title: 'List Entra Configs',
      method: 'GET',
      path: '/api/v1/entra-configs/',
      description: 'List all Entra SSO configurations. Optionally filter by tenant_id.',
      response: `{
  "count": 2,
  "results": [
    {
      "id": 1,
      "tenant_id": "acme",
      "entra_tenant_id": "00000000-0000-0000-0000-000000000000",
      "entra_client_id": "00000000-0000-0000-0000-000000000001",
      "entra_client_secret": "***",
      "enabled": true,
      "allowed_domains": ["acme.com"],
      "created_at": "2026-01-01T00:00:00Z",
      "updated_at": "2026-01-01T00:00:00Z"
    }
  ]
}`,
    },
    {
      title: 'Create Entra Config',
      method: 'POST',
      path: '/api/v1/entra-configs/',
      description: 'Create a new Entra SSO configuration for a tenant.',
      request: `{
  "tenant_id": "acme",
  "entra_tenant_id": "00000000-0000-0000-0000-000000000000",
  "entra_client_id": "00000000-0000-0000-0000-000000000001",
  "entra_client_secret": "my-client-secret",
  "enabled": true,
  "allowed_domains": ["acme.com", "acme.org"]
}`,
      response: `{
  "id": 1,
  "tenant_id": "acme",
  "entra_tenant_id": "00000000-0000-0000-0000-000000000000",
  "entra_client_id": "00000000-0000-0000-0000-000000000001",
  "entra_client_secret": "***",
  "enabled": true,
  "allowed_domains": ["acme.com", "acme.org"],
  "created_at": "2026-04-14T10:00:00Z",
  "updated_at": "2026-04-14T10:00:00Z"
}`,
    },
    {
      title: 'Get Entra Config',
      method: 'GET',
      path: '/api/v1/entra-configs/<id>/',
      description: 'Retrieve a single Entra config by ID.',
      response: `{
  "id": 1,
  "tenant_id": "acme",
  "entra_tenant_id": "00000000-0000-0000-0000-000000000000",
  "entra_client_id": "00000000-0000-0000-0000-000000000001",
  "entra_client_secret": "***",
  "enabled": true,
  "allowed_domains": ["acme.com"],
  "created_at": "2026-01-01T00:00:00Z",
  "updated_at": "2026-01-01T00:00:00Z"
}`,
    },
    {
      title: 'Update Entra Config',
      method: 'PATCH',
      path: '/api/v1/entra-configs/<id>/',
      description: 'Partially update an Entra config. Pass "***" as entra_client_secret to leave it unchanged.',
      request: `{
  "enabled": false,
  "allowed_domains": ["acme.com"],
  "entra_client_secret": "***"
}`,
      response: `{
  "id": 1,
  "tenant_id": "acme",
  "enabled": false,
  "allowed_domains": ["acme.com"],
  "updated_at": "2026-04-14T12:00:00Z"
}`,
    },
    {
      title: 'Delete Entra Config',
      method: 'DELETE',
      path: '/api/v1/entra-configs/<id>/',
      description: 'Permanently delete an Entra config.',
      response: `{
  "success": true
}`,
    },
  ];

  const apiKeyEndpoints = [
    {
      title: 'List API Keys',
      method: 'GET',
      path: '/api/v1/api-keys/',
      description: 'List all API keys. Key values are not returned in listing responses.',
      response: `{
  "count": 1,
  "results": [
    {
      "id": 1,
      "name": "Production Key",
      "description": "Used by the main API",
      "is_active": true,
      "created_at": "2026-01-01T00:00:00Z"
    }
  ]
}`,
    },
    {
      title: 'Create API Key',
      method: 'POST',
      path: '/api/v1/api-keys/',
      description: 'Create a new API key. The key value is auto-generated and returned only on creation.',
      request: `{
  "name": "Production Key",
  "description": "Used by the main API",
  "is_active": true
}`,
      response: `{
  "id": 1,
  "key": "a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2",
  "name": "Production Key"
}`,
    },
    {
      title: 'Update API Key',
      method: 'PATCH',
      path: '/api/v1/api-keys/<id>/',
      description: 'Update an API key name, description, or active status.',
      request: `{
  "name": "Renamed Key",
  "is_active": false
}`,
      response: `{
  "id": 1,
  "name": "Renamed Key",
  "is_active": false
}`,
    },
    {
      title: 'Delete API Key',
      method: 'DELETE',
      path: '/api/v1/api-keys/<id>/',
      description: 'Delete an API key. This immediately revokes access for any service using it.',
      response: `{
  "success": true
}`,
    },
  ];

  const adminUserEndpoints = [
    {
      title: 'List Admin Users',
      method: 'GET',
      path: '/api/v1/admin-users/',
      description: 'List all admin users.',
      response: `{
  "count": 1,
  "results": [
    {
      "id": 1,
      "username": "admin",
      "email": "admin@example.com",
      "is_active": true,
      "is_superuser": true
    }
  ]
}`,
    },
    {
      title: 'Create Admin User',
      method: 'POST',
      path: '/api/v1/admin-users/',
      description: 'Create a new admin user.',
      request: `{
  "username": "ops-user",
  "email": "ops@example.com",
  "password": "secure-password"
}`,
      response: `{
  "id": 2,
  "username": "ops-user"
}`,
    },
    {
      title: 'Current User',
      method: 'GET',
      path: '/api/v1/admin-users/me/',
      description: 'Return details for the currently authenticated admin user.',
      response: `{
  "id": 1,
  "username": "admin",
  "email": "admin@example.com",
  "is_superuser": true
}`,
    },
    {
      title: 'Update Admin User',
      method: 'PATCH',
      path: '/api/v1/admin-users/<id>/',
      description: 'Update an admin user\'s email, password, or active status.',
      request: `{
  "email": "newemail@example.com",
  "password": "new-secure-password"
}`,
      response: `{
  "id": 1,
  "username": "admin",
  "email": "newemail@example.com"
}`,
    },
    {
      title: 'Delete Admin User',
      method: 'DELETE',
      path: '/api/v1/admin-users/<id>/',
      description: 'Delete an admin user. A user cannot delete their own account.',
      response: `{
  "success": true
}`,
    },
  ];

  const auditEndpoints = [
    {
      title: 'List Audit Logs',
      method: 'GET',
      path: '/api/v1/audit-logs/',
      description: 'Return the 200 most recent audit log entries, newest first.',
      response: `{
  "count": 2,
  "results": [
    {
      "id": 42,
      "username": "admin",
      "action": "sso_login",
      "model_type": "entra_config",
      "object_id": 1,
      "object_name": "acme",
      "tenant_id": "acme",
      "ip_address": "1.2.3.4",
      "created_at": "2026-04-14T09:15:00Z"
    }
  ]
}`,
    },
  ];

  const dashboardEndpoints = [
    {
      title: 'Dashboard Stats',
      method: 'GET',
      path: '/api/v1/dashboard/',
      description: 'Summary statistics for the SSO service: tenant counts, API key count, cumulative SSO login and provision counts.',
      response: `{
  "total_tenants": 5,
  "enabled_tenants": 4,
  "total_api_keys": 2,
  "recent_logins": 1234,
  "recent_provisions": 87
}`,
    },
    {
      title: 'Health Check',
      method: 'GET',
      path: '/api/v1/health/',
      description: 'Public endpoint returning the service name and status.',
      response: `{
  "status": "ok",
  "service": "sso-entra"
}`,
    },
  ];

  const analyticsEndpoints = [
    {
      title: 'Usage Analytics',
      method: 'GET',
      path: '/api/v1/analytics/?days=30',
      description: 'SSO usage analytics over a configurable period (max 365 days). Includes SSO logins/provisions by day, all audit events by day, breakdown by action, and top active tenants.',
      response: `{
  "period_days": 30,
  "audit_by_day": [{"date": "2026-04-01", "count": 12}],
  "sso_logins_by_day": [{"date": "2026-04-01", "count": 8}],
  "sso_provisions_by_day": [{"date": "2026-04-01", "count": 2}],
  "audit_by_action": [
    {"action": "sso_login", "count": 240},
    {"action": "sso_provision", "count": 17}
  ],
  "tenant_activity": [
    {"tenant_id": "acme", "count": 95}
  ],
  "total_tenants": 5,
  "enabled_tenants": 4,
  "total_logins": 1234,
  "total_provisions": 87
}`,
    },
  ];

  const postgresEndpoints = [
    {
      title: 'Server Status',
      method: 'GET',
      path: '/api/v1/postgres/status/',
      description: 'Get server version, uptime, database size, connection stats, and configuration.',
      response: `{
  "success": true,
  "postgres": {
    "connected": true,
    "server_info": {
      "version": "16.2",
      "start_time": "2026-04-01T00:00:00+00:00",
      "latency_ms": 0.42
    },
    "database": {"name": "sso", "size_bytes": 8192000, "size_pretty": "8 MB"},
    "connections": {"active": 3, "idle": 2, "total": 5, "max": 100},
    "tables": {"count": 12},
    "config": {"max_connections": "100", "shared_buffers": "128MB"}
  }
}`,
    },
    {
      title: 'List Tables',
      method: 'GET',
      path: '/api/v1/postgres/tables/?page=1&sort=size&search=',
      description: 'List tables with sizes, row counts, and vacuum stats. Supports pagination, sorting (size/name/rows), and search.',
      response: `{
  "success": true,
  "tables": [
    {
      "tablename": "sso_auditlog",
      "size_pretty": "2 MB",
      "row_estimate": 45000,
      "last_vacuum": "Never",
      "last_analyze": "2026-04-10T03:00:00Z"
    }
  ],
  "pagination": {"page": 1, "per_page": 25, "total": 12, "pages": 1}
}`,
    },
    {
      title: 'Active Queries',
      method: 'GET',
      path: '/api/v1/postgres/activity/',
      description: 'Get active queries and connection states from pg_stat_activity.',
      response: `{
  "success": true,
  "activity": [
    {
      "pid": 1234,
      "usename": "sso",
      "state": "idle",
      "query_duration_secs": null,
      "query": "SELECT ..."
    }
  ]
}`,
    },
    {
      title: 'Execute SQL Query',
      method: 'POST',
      path: '/api/v1/postgres/query/',
      description: 'Execute a read-only SQL query. Write operations (DROP, DELETE, INSERT, UPDATE, ALTER, etc.) are blocked.',
      request: `{
  "sql": "SELECT tablename, pg_size_pretty(pg_total_relation_size(quote_ident(tablename))) FROM pg_tables WHERE schemaname = 'public' LIMIT 10"
}`,
      response: `{
  "success": true,
  "columns": ["tablename", "pg_size_pretty"],
  "rows": [
    {"tablename": "sso_auditlog", "pg_size_pretty": "2 MB"}
  ],
  "row_count": 1,
  "duration_ms": 3.14
}`,
    },
  ];

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>API Documentation</Heading>
        <Text color="gray.500">All admin endpoints require <Code>Authorization: Token &lt;token&gt;</Code> unless marked as public.</Text>
      </Box>

      {/* Authentication */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Authentication</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {authEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {authEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* OAuth Flow */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">OAuth / SSO Flow</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {oauthEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {oauthEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* Entra Configs */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Entra Configurations</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {entraConfigEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {entraConfigEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* API Keys */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">API Keys</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {apiKeyEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {apiKeyEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* Admin Users */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Admin Users</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {adminUserEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {adminUserEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* Audit Logs */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Audit Logs</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {auditEndpoints.length} endpoint
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {auditEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* Dashboard & Health */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Dashboard &amp; Health</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {dashboardEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {dashboardEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* Analytics */}
      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Insights</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {analyticsEndpoints.length} endpoint
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {analyticsEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      {/* PostgreSQL */}
      <Box>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">PostgreSQL Status</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {postgresEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {postgresEndpoints.map((ep, i) => <EndpointCard key={i} endpoint={ep} />)}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default ApiDocs;
